import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField as MuiTextField,
  MenuItem,
  Chip,
} from '@mui/material';
import { ReactElement, useState, useCallback, useEffect } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  List,
  TextField,
  useTranslate,
  FunctionField,
  BooleanField,
  useNotify,
  useRefresh,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getAmountRanges,
  createAmountRange,
  updateAmountRange,
  RedPacketType,
  AmountRange,
  RedPacketRecord,
} from '../../../services/redpacketService';
import CreateTypeConfig from './Create';
import EditTypeConfig from './Edit';
import DepositTierList from '../deposit-tier/List';
import TradingTierList from '../trading-tier/List';
import CriticalHitTierList from '../critical-hit-tier/List';
import dayjs from 'dayjs';
import { DATE_FORMAT, TIME_FORMAT } from '../../../../../configs/common.config';

interface TypeConfigProps {
  typeFilter: RedPacketType;
}
const RedPacketTypeConfigList = (props: TypeConfigProps) => {
  const { typeFilter } = props;
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [popup, setPopup] = useState<ReactElement>();
  const [data, setData] = useState<AmountRange[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAmountRanges(typeFilter);
      setData(Array.isArray(result) ? result : []);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    if (window.confirm(translate('module.redpacket.confirmDeletion'))) {
      try {
        await updateAmountRange({ id, isActive: false });
        notify(translate('module.redpacket.deleteSuccess'), { type: 'success' });
        fetchData();
      } catch (error: any) {
        notify(error.message || translate('module.redpacket.deleteFailed'), { type: 'error' });
      }
    }
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.typeConfig')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.typeConfig') },
          ]}
          action={
            <PermissionGuard resource="redpacket" action="create" fallback={<></>}>
              {(typeFilter === RedPacketType.TWITTER_BINDING ||
                typeFilter === RedPacketType.TWITTER_FOLLOW ||
                typeFilter === RedPacketType.DEPOSIT ||
                typeFilter === RedPacketType.TRADING) && (
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(
                      <CreateTypeConfig
                        typeFilter={typeFilter}
                        onClose={() => {
                          setPopup(undefined);
                          fetchData();
                        }}
                      />
                    );
                  }}
                />
              )}
            </PermissionGuard>
          }
        />
        {(typeFilter === RedPacketType.TWITTER_BINDING ||
          typeFilter === RedPacketType.TWITTER_FOLLOW ||
          typeFilter === RedPacketType.DEPOSIT ||
          typeFilter === RedPacketType.TRADING) && (
          <Card sx={{ mt: 3, pb: 2 }}>
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid rowClick={false} data={data} isLoading={loading} bulkActionButtons={false}>
                <FunctionField
                  label={translate('module.redpacket.poolType')}
                  render={(record: AmountRange) => {
                    // 根据类型判断资金池
                    const poolMap: Record<RedPacketType, string> = {
                      [RedPacketType.TWITTER_BINDING]: translate('module.redpacket.sharedPoolLabel'),
                      [RedPacketType.TWITTER_FOLLOW]: translate('module.redpacket.sharedPoolLabel'),
                      [RedPacketType.DEPOSIT]: translate('module.redpacket.sharedPoolLabel'),
                      [RedPacketType.TRADING]: translate('module.redpacket.sharedPoolLabel'),
                      [RedPacketType.CRITICAL_HIT]: translate('module.redpacket.criticalHitPoolLabel'),
                    };
                    return poolMap[record.redPacketType] || '-';
                  }}
                />
                <TextField source="minAmount" label={translate('module.redpacket.minAmountLabel')} />
                <TextField source="maxAmount" label={translate('module.redpacket.maxAmountLabel')} />
                <FunctionField
                  label={translate('module.redpacket.status')}
                  render={(record: AmountRange) => (
                    <Chip
                      label={record.isActive ? translate('module.redpacket.running') : translate('module.redpacket.closed')}
                      color={record.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />
                <FunctionField
                  source="updatedAt"
                  label={translate('module.redpacket.updatedTime')}
                  minWidth={180}
                  maxWidth={200}
                  render={(record) =>
                    record.updatedAt ? dayjs(record.updatedAt).format(TIME_FORMAT) : ''
                  }
                />
                <TextField source="description" label={translate('module.redpacket.descriptionCol')} />
                <FunctionField
                  label={translate('module.redpacket.action')}
                  render={(record: AmountRange) => (
                    <Stack direction="row" spacing={1}>
                      <PermissionButton resource="redpacket" action="update">
                        <EditButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setPopup(
                              <EditTypeConfig
                                id={record.id}
                                typeFilter={typeFilter}
                                record={record}
                                onClose={() => {
                                  setPopup(undefined);
                                  fetchData();
                                }}
                              />
                            );
                          }}
                        />
                      </PermissionButton>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </Card>
        )}

        {typeFilter === RedPacketType.DEPOSIT && (
          <Box sx={{ mt: 14 }}>
            {' '}
            {/* 核心：用 Box 包裹并设置间距 */}
            <DepositTierList />
          </Box>
        )}
        {typeFilter === RedPacketType.TRADING && (
          <Box sx={{ mt: 14 }}>
            {' '}
            {/* 核心：用 Box 包裹并设置间距 */}
            <TradingTierList />
          </Box>
        )}
        {typeFilter === RedPacketType.CRITICAL_HIT && (
          <Box sx={{ mt: 0 }}>
            {' '}
            {/* 核心：用 Box 包裹并设置间距 */}
            <CriticalHitTierList />
          </Box>
        )}

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

// @ts-ignore
export default RedPacketTypeConfigList;
